import { useCallback, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ModalShell } from '../shared/ModalShell';
import { NO_TREATMENT_START_ON_RECORD } from './virtualAppointmentWeeksHint';
import type { AlignerProgress } from './virtualAppointmentTriggerBaseline';
import { VirtualAppointmentEditor } from './VirtualAppointmentEditor';
import { useCreateVirtualAppointment } from './useCreateVirtualAppointment';
import {
    useVirtualAppointmentImageUpload,
    useVirtualAppointmentTypeOptions
} from './useVirtualAppointmentEditorSupport';
import { alignerLimitOf } from './virtualAppointmentTriggerAhead';
import {
    createEmptyDraft,
    draftToPlan,
    hasDraftErrors,
    triggerContextFor,
    validateDraft,
    type VirtualAppointmentDraft,
    type VirtualAppointmentDraftPatch
} from './virtualAppointmentDraft';

const TITLE_ID = 'va-add-title';
const CANCEL_BUTTON =
    'bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50';
const CONFIRM_BUTTON =
    'bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

interface AddVirtualAppointmentModalProps {
    patientId: string;
    /** UTC day the Treatment phase began, which week triggers count from; null when unknown. */
    treatmentStartDate: string | null;
    /** Whether the patient is linked to Dental Monitoring, which no-go triggers count on. */
    dmLinked: boolean;
    /** Shown under an aligner trigger, when known. */
    aligners: AlignerProgress;
    onClose: () => void;
}

// One VA planned outside the RDI/SDI wizard, through the wizard's own editor and draft rules.
export function AddVirtualAppointmentModal({
    patientId,
    treatmentStartDate,
    dmLinked,
    aligners,
    onClose
}: AddVirtualAppointmentModalProps) {
    const [draft, setDraft] = useState<VirtualAppointmentDraft>(createEmptyDraft);
    const [revealErrors, setRevealErrors] = useState(false);
    const typeOptions = useVirtualAppointmentTypeOptions();
    const handleImageUpload = useVirtualAppointmentImageUpload(patientId);
    const { submitting, error: apiError, create } = useCreateVirtualAppointment(patientId);
    const errors = validateDraft(
        draft,
        triggerContextFor(treatmentStartDate, dmLinked, {
            alignerLimit: alignerLimitOf(aligners.total),
            weeksUnavailable: treatmentStartDate === null
        })
    );
    const invalid = hasDraftErrors(errors);
    const update = useCallback(
        (_key: string, patch: VirtualAppointmentDraftPatch) =>
            setDraft((prev) => ({ ...prev, ...patch })),
        []
    );

    const handleSubmit = () => {
        setRevealErrors(true);
        if (!invalid && !submitting) create(draftToPlan(draft), onClose);
    };

    return (
        <ModalShell
            widthClassName="max-w-[640px]"
            dismissible={!submitting}
            onDismiss={onClose}
            labelledBy={TITLE_ID}
            panelClassName="rounded-lg flex flex-col h-[min(720px,88vh)] overflow-hidden"
        >
            <div className="flex justify-between items-center px-5 pt-5">
                <h2 id={TITLE_ID} className="text-xl font-bold text-gray-900">
                    Add VA
                </h2>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    aria-label="Close"
                    className="text-gray-400 hover:text-gray-800 disabled:opacity-50"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>
            </div>
            <p className="px-5 pt-1 text-sm text-gray-600">
                The VA stays planned and opens when its trigger is reached.
            </p>
            <VirtualAppointmentEditor
                draft={draft}
                errors={errors}
                typeOptions={typeOptions}
                revealErrors={revealErrors}
                onChange={update}
                onImageUpload={handleImageUpload}
                stackTypeAndTrigger
                treatmentStartDate={treatmentStartDate}
                dmLinked={dmLinked}
                aligners={aligners}
                // Only opened from the Treatment workspaces, where a missing start means none on record.
                treatmentStartUnavailableHint={
                    treatmentStartDate === null ? NO_TREATMENT_START_ON_RECORD : undefined
                }
            />
            {apiError && (
                <p role="alert" className="px-5 text-sm text-red-500">
                    {apiError}
                </p>
            )}
            <div className="flex justify-end items-center gap-3 px-5 pb-5 pt-4">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className={CANCEL_BUTTON}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || (revealErrors && invalid)}
                    className={CONFIRM_BUTTON}
                >
                    {submitting ? 'Adding…' : 'Add VA'}
                </button>
            </div>
        </ModalShell>
    );
}
